import { useMemo } from 'react';
import {
  FEATURE_REGISTRY,
  MergedFeatureConfig,
  SubscriptionTier,
  FEATURE_DEPENDENCY_MAP,
  DataScope,
} from '@everyday-os/shared';
import { useFeatureConfigContext } from '@/contexts/FeatureConfigContext';

const TIER_RANK: Record<SubscriptionTier, number> = {
  [SubscriptionTier.FREE]: 0,
  [SubscriptionTier.BASIC]: 1,
  [SubscriptionTier.PREMIUM]: 2,
};

function stableHash(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = ((h * 31) + id.charCodeAt(i)) >>> 0;
  return h % 100;
}

export function useFeatureAccess(
  householdTier: SubscriptionTier,
  hiddenFeatures: string[],
  opts?: {
    householdId?: string;
    householdModules?: string[];
    activeScope?: DataScope;
  }
): {
  visibleNavFeatures: MergedFeatureConfig[];
  lockedNavFeatures: MergedFeatureConfig[];
  isAccessible: (featureId: string) => boolean;
  isInMaintenance: (featureId: string) => boolean;
  maintenanceMessage: (featureId: string) => string | undefined;
  isDisabled: (featureId: string) => boolean;
  getDependents: (featureId: string) => string[];
} {
  const { configs } = useFeatureConfigContext();

  return useMemo(() => {
    const getConfig = (featureId: string): MergedFeatureConfig | undefined => configs.get(featureId);

    const isDisabled = (featureId: string): boolean =>
      getConfig(featureId)?.status === 'disabled';

    const isInMaintenance = (featureId: string): boolean =>
      getConfig(featureId)?.status === 'maintenance';

    const maintenanceMessage = (featureId: string): string | undefined =>
      getConfig(featureId)?.maintenanceMessage;

    const getDependents = (featureId: string): string[] =>
      Object.entries(FEATURE_DEPENDENCY_MAP)
        .filter(([, deps]) => (deps as string[]).includes(featureId))
        .map(([key]) => key);

    const isAccessible = (featureId: string): boolean => {
      const config = getConfig(featureId);
      const registryEntry = FEATURE_REGISTRY.find(f => f.id === featureId);
      if (!registryEntry) return false;

      // 1. Disabled → never accessible
      if (config?.status === 'disabled') return false;

      // 2. Addon category check
      if (config?.featureCategory === 'addon') {
        const includedInTiers = config.addonIncludedInTiers ?? [];
        if (includedInTiers.includes(householdTier)) return true;
        if (opts?.householdModules?.includes(featureId)) return true;
        return false;
      }

      // 3. Core tier check
      const effectiveMinTier = config?.effectiveMinTier ?? registryEntry.minTier;
      if (TIER_RANK[householdTier] < TIER_RANK[effectiveMinTier]) return false;

      // 4. Scope check
      if (config?.effectiveScopes && opts?.activeScope) {
        const scope = opts.activeScope === 'PERSONAL' ? 'personal'
          : opts.activeScope === 'SHARED' ? 'household'
          : null; // BOTH — skip scope check
        if (scope && !config.effectiveScopes.includes(scope)) return false;
      }

      // 5. Rollout check
      if (opts?.householdId) {
        const hash = stableHash(opts.householdId);
        if (config?.rolloutHouseholdIds?.includes(opts.householdId)) return true;
        const rolloutByTier = config?.rolloutByTier as Partial<Record<SubscriptionTier, number>> | undefined;
        const tierRollout = rolloutByTier?.[householdTier];
        const threshold = tierRollout !== undefined ? tierRollout : (config?.rolloutPercent ?? 100);
        if (hash >= threshold) return false;
      }

      return true;
    };

    const allConfigs: MergedFeatureConfig[] = FEATURE_REGISTRY.map(entry =>
      configs.get(entry.id) ?? {
        ...entry,
        featureCategory: 'core' as const,
        status: 'active' as const,
        effectiveMinTier: entry.minTier,
        effectiveScopes: ['personal', 'household'] as Array<'personal' | 'household'>,
        addonIncludedInTiers: [],
        rolloutPercent: 100,
        rolloutByTier: {},
        rolloutHouseholdIds: [],
        scheduledChanges: [],
      }
    );

    const visibleNavFeatures = allConfigs.filter(f =>
      f.status !== 'disabled' &&
      isAccessible(f.id) &&
      !hiddenFeatures.includes(f.id)
    );

    const lockedNavFeatures = allConfigs.filter(f => !isAccessible(f.id));

    return {
      visibleNavFeatures,
      lockedNavFeatures,
      isAccessible,
      isInMaintenance,
      maintenanceMessage,
      isDisabled,
      getDependents,
    };
  }, [configs, householdTier, hiddenFeatures, opts?.householdId, opts?.householdModules, opts?.activeScope]);
}
